import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { InputField, TextareaField, SelectField } from '@/pages/dashboard/components/FormFields';
import OpeningHoursEditor, {
  emptyOpeningHours,
  normalizeOpeningHours,
  type OpeningHoursValue,
} from '@/pages/dashboard/components/OpeningHoursEditor';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface Category {
  id: string;
  name: string;
}

function BusinessEdit() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [hasBusiness, setHasBusiness] = useState(true);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    description: '',
    logo_url: '',
    category_id: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    address: '',
    city: '',
    country: 'Togo',
  });
  const [hours, setHours] = useState<OpeningHoursValue>(emptyOpeningHours());
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const [catRes, bizRes] = await Promise.all([
          supabase.from('categories').select('id, name').is('parent_id', null).order('name'),
          supabase.from('businesses').select('*').eq('owner_id', user.id).maybeSingle(),
        ]);

        if (!mounted) return;

        if (catRes.data) {
          setCategories(catRes.data as Category[]);
        }

        if (bizRes.data) {
          const b = bizRes.data;
          setBusinessId(b.id);
          setHasBusiness(true);
          setForm({
            name: b.name || '',
            description: b.description || '',
            logo_url: b.logo_url || '',
            category_id: b.category_id || '',
            phone: b.phone || '',
            whatsapp: b.whatsapp || '',
            email: b.email || '',
            website: b.website || '',
            address: b.address || '',
            city: b.city || '',
            country: b.country || 'Togo',
          });
          setHours(normalizeOpeningHours(b.opening_hours));
        } else {
          setHasBusiness(false);
        }
      } catch {
        // erreur silencieuse, l'état loading suffit
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) return;
    if (!form.name.trim()) {
      setStatus('error');
      setErrorMsg('Le nom de l\'entreprise est obligatoire.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const { error } = await supabase
      .from('businesses')
      .update({
        name: form.name.trim(),
        description: form.description.trim() || null,
        logo_url: form.logo_url.trim() || null,
        category_id: form.category_id || null,
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        opening_hours: hours,
      })
      .eq('id', businessId);

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    setStatus('success');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!hasBusiness) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
            <i className="ri-store-2-line text-3xl text-primary-400" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">
            Aucune entreprise enregistrée
          </h1>
          <p className="text-sm text-foreground-400 mb-6">
            Vous devez d&apos;abord inscrire votre entreprise avant de pouvoir la modifier.
          </p>
          <Link
            to="/auth/register"
            className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Inscrire mon entreprise
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Mon entreprise</h1>
        <p className="text-sm text-foreground-400 mt-1">
          Modifiez les informations publiques de votre entreprise, vos coordonnées et vos horaires.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {/* Informations générales */}
        <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
          <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
              <i className="ri-information-line text-primary-400" />
            </span>
            Informations générales
          </h2>

          <InputField
            id="name"
            label="Nom de l'entreprise"
            icon="ri-store-line"
            value={form.name}
            onChange={(v) => updateField('name', v)}
            placeholder="Mon Entreprise SARL"
            required
          />

          <SelectField
            id="category_id"
            label="Catégorie"
            icon="ri-folder-line"
            value={form.category_id}
            onChange={(v) => updateField('category_id', v)}
            placeholder="Sélectionnez une catégorie"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />

          <TextareaField
            id="description"
            label="Description"
            icon="ri-file-text-line"
            value={form.description}
            onChange={(v) => updateField('description', v)}
            placeholder="Décrivez votre activité, vos services, vos spécialités..."
            rows={4}
            maxLength={1000}
          />

          <InputField
            id="logo_url"
            label="URL du logo"
            icon="ri-image-line"
            value={form.logo_url}
            onChange={(v) => updateField('logo_url', v)}
            placeholder="https://..."
            hint="Lien direct vers l'image de votre logo."
          />
        </section>

        {/* Coordonnées */}
        <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
          <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
              <i className="ri-contacts-line text-accent-400" />
            </span>
            Coordonnées
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              id="phone"
              label="Téléphone"
              icon="ri-phone-line"
              type="tel"
              value={form.phone}
              onChange={(v) => updateField('phone', v)}
              placeholder="+228 90 00 00 00"
            />
            <InputField
              id="whatsapp"
              label="WhatsApp"
              icon="ri-whatsapp-line"
              type="tel"
              value={form.whatsapp}
              onChange={(v) => updateField('whatsapp', v)}
              placeholder="+228 90 00 00 00"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              id="email"
              label="Email"
              icon="ri-mail-line"
              type="email"
              value={form.email}
              onChange={(v) => updateField('email', v)}
              placeholder="contact@entreprise.tg"
            />
            <InputField
              id="website"
              label="Site web"
              icon="ri-global-line"
              value={form.website}
              onChange={(v) => updateField('website', v)}
              placeholder="https://entreprise.tg"
            />
          </div>
        </section>

        {/* Localisation */}
        <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
          <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
              <i className="ri-map-pin-line text-secondary-400" />
            </span>
            Localisation
          </h2>

          <TextareaField
            id="address"
            label="Adresse complète"
            icon="ri-road-map-line"
            value={form.address}
            onChange={(v) => updateField('address', v)}
            placeholder="123 Rue Principale, Quartier..."
            rows={2}
            maxLength={500}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              id="city"
              label="Ville"
              icon="ri-map-pin-2-line"
              value={form.city}
              onChange={(v) => updateField('city', v)}
              placeholder="Lomé"
            />
            <InputField
              id="country"
              label="Pays"
              icon="ri-earth-line"
              value={form.country}
              onChange={(v) => updateField('country', v)}
              placeholder="Togo"
            />
          </div>
        </section>

        {/* Horaires */}
        <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
          <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
              <i className="ri-time-line text-accent-400" />
            </span>
            Horaires d'ouverture
          </h2>
          <p className="text-sm text-foreground-400">
            Activez les jours où votre entreprise est ouverte et définissez les horaires.
          </p>
          <OpeningHoursEditor value={hours} onChange={setHours} />
        </section>

        {status === 'error' && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
            {errorMsg}
          </div>
        )}
        {status === 'success' && (
          <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400">
            Entreprise mise à jour avec succès.
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}

export default BusinessEdit;